import { InteractiveBoard } from './InteractiveBoard';
import { missionSelection } from '../host/mission-selection';
import { MissionBoard } from './MissionBoard';
import { useState } from 'react';
import type { JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AppGlyph, EmptyState, Icon, RichText, appTileStyle } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { NoWorkspace } from '../desk/NoWorkspace';
import { AttachedDocument } from '../documents/AttachedDocument';
import { findManifest } from '../host/registry';
import { formatParisTime } from '../lib/clock';
import {
  currentActivityQueryKey,
  useActivityCheck,
  useCurrentActivity,
  useInteractiveSessions,
  useDeskApps,
} from '../lib/queries';
import type { CheckFinding, ContactsReviewResult, CurrentActivity } from '../lib/queries';
import { reviewScopeOf } from '../lib/reviewScope';
import { toolReviewFit } from './reviewSubject';
import { ApiError, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import './activity.css';

/**
 * **La consigne du jour, telle qu'un élève la lit en s'asseyant.**
 *
 * Avant cet écran, un élève qui se connectait voyait un bureau, des outils, et rien qui lui dise
 * ce qu'on attendait de lui. C'est le manque que l'enseignant a nommé : « je peux moi créer une
 * activité […] et eux ils peuvent corriger les erreurs qui sont dedans, et un bouton pour valider
 * si effectivement il y a plus d'erreur ».
 *
 * Ce composant est monté à **deux endroits**, et c'est délibéré :
 *
 * 1. sur le **bureau nu**, en grand, au centre — c'est la première chose lue en arrivant ;
 * 2. dans une **fenêtre** ouverte par le dock (`/activity`) — sans quoi la consigne disparaîtrait
 *    à la seconde où l'élève ouvre l'outil pour travailler, avec elle le bouton « J'ai terminé ».
 *
 * Un seul composant pour les deux : deux rédactions du même écran auraient divergé au premier
 * changement de texte.
 *
 * **Aucune app n'est nommée ici** (règle 1 de `CLAUDE.md`). `activity.appId` est un identifiant
 * opaque, choisi par l'enseignant dans le registre généré ; le nom et l'icône affichés sur le
 * bouton viennent du manifeste (`findManifest`), exactement comme dans le dock. Une app installée
 * demain devient un outil d'activité sans qu'une ligne de ce fichier ne change.
 *
 * **Ce que cet écran ne dira jamais : « tout est correct ».** Voir `CheckReport` plus bas.
 *
 * **Il est aussi monté sous les yeux de l'enseignant** — l'icône « Activité du jour » est dans son
 * dock, exprès : il a besoin de lire ce que sa classe lit. Ses phrases changent donc de lecteur
 * sans qu'aucun appel à `t()` ne le sache (`src/messages.ts`, la variante `.teacher`) : « Votre
 * enseignant n'a pas encore lancé d'activité » devient « Vous n'avez pas encore lancé d'activité
 * dans cette classe », et le bouton qui suit ouvre la section « Activités ».
 */

/** L'heure d'un horodatage venu de Postgres, à Paris — ou `null` si la chaîne est illisible, ce
 * que l'appelant traduit par une absence d'affichage plutôt que par une heure inventée. */
function parisTimeOf(iso: string | null): string | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : formatParisTime(new Date(ms));
}

/** Le bouton qui ouvre l'outil de la consigne. Rendu **uniquement** quand l'enseignant en a
 * désigné un : un bouton mort serait pire que pas de bouton. Quand l'outil existe mais n'est pas
 * encore ouvert à la classe, on le dit — c'est une information que l'élève peut transmettre à son
 * enseignant, pas une porte fermée sans explication. */
function OpenToolButton({ appId }: { appId: string }): JSX.Element {
  const { classId, openApp } = useDesk();
  const deskApps = useDeskApps(classId);
  const manifest = findManifest(appId);
  const name = manifest?.name ?? appId;
  const unlocked = (deskApps.data?.appIds ?? []).includes(appId);

  if (!unlocked) {
    return <p className="activity-locked">{t('activity.openLocked', { name })}</p>;
  }
  return (
    <button
      type="button"
      className="ndrc-button ndrc-button--primary activity-open"
      onClick={() => {
        openApp(appId);
      }}
    >
      {/* La marque de l'app, peinte par son propre manifeste — la même tuile que dans le dock et
          dans la liste de l'enseignant, jamais une icône que le cœur choisirait pour elle. */}
      {manifest === undefined ? null : (
        <span className="activity-tile" style={appTileStyle(manifest)} aria-hidden="true">
          <AppGlyph app={manifest} size={13} />
        </span>
      )}
      {t('activity.open', { name })}
    </button>
  );
}

/**
 * **Ce que le bouton « Vérifier » relit — dit AVANT le clic, et quoi qu'il arrive.**
 *
 * `activities.check` et `contacts.review` relisent les fiches clients de l'espace de l'élève, et
 * rien d'autre. Le bouton, lui, s'affiche sur TOUTE activité : le 8 septembre 2026, une consigne
 * portant sur l'entretien téléphonique le proposait encore, et l'élève qui cliquait recevait un
 * compte rendu sur seize fiches clients sans rapport avec le travail demandé — sans aucun moyen
 * de s'en apercevoir.
 *
 * Le correctif ne construit pas une seconde relecture : il fait dire à celle-ci sur quoi elle
 * porte. La phrase change selon ce que l'outil du jour DÉCLARE travailler (`reviewSubject.ts`,
 * jamais un `if (appId === …)`), et l'avertissement disparaît de lui-même quand l'outil touche
 * bien aux fiches clients.
 *
 * Le bouton reste offert, et actif : un élève a le droit de relire ses fiches pendant une séance
 * d'appels. Ce qu'il n'avait pas, c'était le moyen de savoir ce qu'il relisait.
 */
function ReviewSubject({ appId }: { appId: string | null }): JSX.Element {
  const manifest = appId === null ? undefined : findManifest(appId);
  const fit = toolReviewFit(appId, manifest);
  const name = manifest?.name ?? appId ?? '';

  return (
    <>
      <p className="ndrc-hint">{t('activity.checkIntro')}</p>
      {fit === 'covered' ? null : (
        <p className="activity-offsubject">
          {/* La même pastille ambre que « Ce qui n'a pas été relu » plus bas : sur cet écran, cette
              couleur veut dire « la machine ne répond pas de ça ». Elle veut dire la même chose
              ici. */}
          <span className="activity-dot activity-dot--warning" aria-hidden="true" />
          <span>
            {fit === 'offSubject'
              ? t('activity.checkOffSubject', { name })
              : fit === 'unknownTool'
                ? t('activity.checkUnknownTool')
                : t('activity.checkNoToolSubject')}
          </span>
        </p>
      )}
    </>
  );
}

/**
 * Le compte rendu de la vérification — et sa **limite, dite à l'écran**.
 *
 * Trois blocs, toujours dans cet ordre, y compris quand il n'y a rien à corriger :
 * ce qui ne va pas, **ce qui a été relu**, **ce qui ne l'a pas été**. Les deux derniers viennent du
 * serveur (`verified`/`notVerified`, dérivés des règles elles-mêmes) et non de `fr.json` : une
 * liste recopiée à côté du code finit toujours par mentir sur ce que le code fait vraiment.
 *
 * Le mot « correct » n'apparaît nulle part. Quand aucune erreur de forme n'est trouvée, l'écran dit
 * « rien de mal écrit dans vos fiches », puis, immédiatement en dessous, ce qui n'a pas été
 * regardé. Un élève à qui on répondrait « c'est bon » alors que seule la forme a été contrôlée
 * apprendrait quelque chose de faux — et le croirait, parce que la machine l'a dit.
 */
function FindingList({ findings }: { findings: readonly CheckFinding[] }): JSX.Element {
  return (
    <ul className="activity-findings">
      {findings.map((finding) => (
        <li key={`${finding.ruleId}:${finding.subjectId}`} className="activity-finding">
          <span className={`activity-dot activity-dot--${finding.severity}`} aria-hidden="true" />
          <span>{finding.message}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * L'état de la relecture PAR JUGEMENT, tenu à la main plutôt que par une requête React Query.
 * Raison : `contacts.review` est une **mutation** (elle dépense de l'argent et écrit au journal de
 * dépense), et une mutation ne doit jamais être rejouée par un remontage de composant, un retour
 * de focus, ou une reconnexion réseau — trois choses qu'une requête fait volontiers.
 */
interface ReviewState {
  busy: boolean;
  result: ContactsReviewResult | null;
  /** Une panne RÉELLE (réseau, session expirée). Un refus attendu — pas de clé, plafond atteint —
   * arrive dans `result.status === 'unavailable'` avec sa phrase française, jamais ici. */
  error: string | null;
}

const NO_REVIEW: ReviewState = { busy: false, result: null, error: null };

function LegacyActivityBoard(): JSX.Element {
  const { me, classId, observing } = useDesk();
  return (
    <>
      {me.role === 'student' && !observing && classId ? <MissionBoard classId={classId} /> : null}
      {observing || !missionSelection() ? <CurrentActivityBoard /> : null}
    </>
  );
}
function CurrentActivityBoard(): JSX.Element | null {
  const { classId, me, openTeacher, observing } = useDesk();
  const queryClient = useQueryClient();
  const current = useCurrentActivity(classId);
  const interactive = useInteractiveSessions(classId);
  // En observation, les deux relectures portent sur le fichier de l'ÉLÈVE : c'est le compte rendu
  // que l'enseignant vient lire, pas le sien. Les deux opérations n'honorent `userId` que pour un
  // enseignant, et revérifient tout côté serveur — voir `host/reviewWork.ts`.
  const check = useActivityCheck(classId, observing);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<ReviewState>(NO_REVIEW);

  /**
   * Un seul bouton, deux relectures, dans cet ordre — et l'ordre compte.
   *
   * 1. La vérification AUTOMATIQUE part d'abord : elle est gratuite, immédiate, et ses constats
   *    s'affichent pendant que l'IA réfléchit encore.
   * 2. La relecture PAR JUGEMENT suit, et **seulement si** l'élève a bien un fichier à faire
   *    relire (`data === null` veut dire « aucun espace de travail ») : on ne paie jamais un appel
   *    pour un fichier qui n'existe pas.
   *
   * L'élève peut recliquer autant de fois qu'il veut — c'est la décision d'enseignant du
   * 2026-09-07. Le plafond de dépense par classe et par jour est ce qui borne ce « autant de fois
   * qu'il veut », côté serveur, jamais ce bouton.
   */
  async function onVerify(): Promise<void> {
    setReview({ busy: true, result: null, error: null });
    const outcome = await check.refetch();
    if (outcome.data === null || outcome.data === undefined) {
      setReview(NO_REVIEW);
      return;
    }
    try {
      const result = await mutateCore<ContactsReviewResult>(
        'contacts.review',
        observing !== null
          ? { classId: observing.classId, userId: observing.userId }
          : classId === undefined
            ? {}
            : { classId },
      );
      setReview({ busy: false, result, error: null });
    } catch (cause) {
      setReview({
        busy: false,
        result: null,
        error: cause instanceof ApiError ? cause.message : t('activity.aiFailed'),
      });
    }
  }

  const activity: CurrentActivity | null | undefined = current.data?.activity;

  /**
   * « J'ai terminé » et « Je n'avais pas fini », le même chemin dans les deux sens.
   *
   * Le second manquait : le bouton disparaissait après un clic et rien ne permettait de se dédire.
   * Sur trente postes, un clic malheureux est l'accident le plus banal qui soit, et il restait
   * affiché à l'enseignant comme un travail rendu. Rien n'est effacé côté serveur — un second
   * événement est écrit, et c'est le plus récent des deux qui compte (`activities.reopen`).
   */
  async function setDone(id: string, done: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await mutateCore(done ? 'activities.complete' : 'activities.reopen', { activityId: id });
      await queryClient.invalidateQueries({ queryKey: currentActivityQueryKey });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('desk.errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  if (current.isPending) return <p className="activity-notice">{t('activity.loading')}</p>;
  if (current.isError) {
    return (
      <p className="ndrc-error">
        {current.error instanceof Error ? current.error.message : t('desk.errorTitle')}
      </p>
    );
  }

  if (!activity && interactive.data?.sessions.length) return null;
  const doneAt = parisTimeOf(activity?.completedAt ?? null);
  const endsAt = parisTimeOf(activity?.endsAt ?? null);
  const startedAt = parisTimeOf(activity?.startedAt ?? null);

  return (
    <div className="activity">
      {activity === null || activity === undefined ? (
        <EmptyState title={t('activity.noneTitle')} hint={t('activity.noneHint')}>
          {/* L'élève n'a rien à faire de cet écran : c'est une attente, pas une tâche. L'adulte,
              lui, vient de lire ce que sa classe a sous les yeux — le bouton lui évite d'aller le
              chercher ailleurs. */}
          {me.role === 'teacher' ? (
            <button
              type="button"
              className="ndrc-button ndrc-button--primary"
              onClick={() => {
                openTeacher('activities');
              }}
            >
              {t('activity.noneAction')}
            </button>
          ) : null}
        </EmptyState>
      ) : (
        <section className="activity-brief">
          <header className="activity-head">
            <span className={`activity-badge${doneAt === null ? '' : ' activity-badge--done'}`}>
              {doneAt === null ? t('activity.badgeRunning') : t('activity.badgeDone')}
            </span>
            <h2 className="activity-title">{activity.title}</h2>
            {activity.paused ? <p role="status">{t('activity.paused')}</p> : null}
            <p className="activity-when">
              {startedAt === null ? null : t('activity.started', { time: startedAt })}
              {endsAt === null ? null : (
                <span className="activity-deadline">{t('activity.until', { time: endsAt })}</span>
              )}
            </p>
          </header>

          <p className="ndrc-label">{t('activity.instructionsLabel')}</p>
          {/* La consigne est MISE EN FORME (`RichText`, `@ndrc/ui`) : le champ s'appelle
              `instructionsMd` d'un bout à l'autre de la chaîne, l'écran tient enfin cette
              promesse. Paragraphes, listes, gras, italique — jamais de HTML interprété, jamais de
              lien cliquable : voir le commentaire de tête de `packages/ui/src/rich-text.tsx`. */}
          {activity.instructionsMd === null || activity.instructionsMd.trim() === '' ? (
            <p className="activity-instructions">{t('activity.noInstructions')}</p>
          ) : (
            <RichText text={activity.instructionsMd} className="activity-instructions" />
          )}

          {/* **Le document joint à la consigne** — l'autre moitié de ce que « faire passer un
              document » veut dire. Il s'ouvre ici, à l'endroit où l'élève lit ce qu'on lui demande,
              et pas seulement depuis « Documents » : aller le chercher dans une autre fenêtre au
              milieu d'un travail, c'est la friction qui fait qu'on ne l'ouvre pas.

              `activity.document` est `null` quand rien n'est joint, quand le document a été retiré
              depuis, ou quand ce lecteur n'a pas le droit de le voir — les trois s'affichent
              pareil (rien), et c'est voulu : voir `activities.current` (`@ndrc/core-domain`). */}
          {activity.document === null ? null : (
            <>
              <p className="ndrc-label">{t('activity.documentLabel')}</p>
              <AttachedDocument document={activity.document} />
            </>
          )}

          <div className="activity-actions">
            {activity.appId === null ? (
              <p className="activity-locked">{t('activity.noTool')}</p>
            ) : (
              <OpenToolButton appId={activity.appId} />
            )}
            <button
              type="button"
              className="ndrc-button"
              disabled={check.isFetching || review.busy}
              onClick={() => {
                void onVerify();
              }}
            >
              <Icon name="check" size={14} strokeWidth={1.9} />
              {check.isFetching || review.busy
                ? t('activity.checkBusy')
                : check.data === undefined
                  ? t('activity.check')
                  : t('activity.checkAgain')}
            </button>
            <button
              type="button"
              className="ndrc-button"
              disabled={busy || activity.paused}
              onClick={() => {
                void setDone(activity.id, doneAt === null);
              }}
            >
              {busy
                ? t('activity.doneBusy')
                : doneAt === null
                  ? t('activity.done')
                  : t('activity.undone')}
            </button>
          </div>

          {doneAt === null ? null : (
            <p className="ndrc-ok">{t('activity.doneAt', { time: doneAt })}</p>
          )}
          {error === null ? null : <p className="ndrc-error">{error}</p>}
          <ReviewSubject appId={activity.appId} />
        </section>
      )}

      <CheckReport check={check} review={review} />
    </div>
  );
}

/** Extrait du corps principal pour que la vérification reste lisible d'un seul tenant : c'est la
 * partie de cet écran qui doit être relue avec le plus d'attention. */
function CheckReport({
  check,
  review,
}: {
  check: ReturnType<typeof useActivityCheck>;
  review: ReviewState;
}): JSX.Element | null {
  if (check.isError) {
    return (
      <p className="ndrc-error">
        {check.error instanceof Error ? check.error.message : t('desk.errorTitle')}
      </p>
    );
  }
  // `undefined` : jamais vérifié depuis l'ouverture de l'écran. Rien à montrer — surtout pas un
  // cadre vide qui laisserait croire qu'un contrôle a eu lieu.
  if (check.data === undefined) return null;
  // `null` : aucun espace de travail. Le contrat de l'opération, jamais une erreur.
  if (check.data === null) {
    return (
      <section className="activity-report">
        <NoWorkspace
          titleKey="activity.checkNoWorkspaceTitle"
          hintKey="activity.checkNoWorkspaceHint"
        />
      </section>
    );
  }

  const { findings, scannedContacts } = check.data;
  const errors = findings.filter((f) => f.severity === 'error');
  const warnings = findings.filter((f) => f.severity === 'warning');
  // Les deux listes sortent d'UNE paire cohérente, choisie et jamais assemblée ici : avant ce
  // correctif, l'écran affichait la liste « pas relu » d'AVANT la relecture par jugement à côté
  // des points que cette relecture venait de regarder — la même question annoncée relue et pas
  // relue, dans le même panneau. Voir `lib/reviewScope.ts`.
  const { looked, notLooked } = reviewScopeOf(check.data, review.result);

  return (
    <section className="activity-report">
      <header className="activity-report-head">
        <h3 className="activity-report-title">{t('activity.checkTitle')}</h3>
        <p className="activity-when">
          {scannedContacts === 1
            ? t('activity.checkScannedOne')
            : t('activity.checkScannedMany', { n: scannedContacts })}
        </p>
      </header>

      {errors.length === 0 && warnings.length === 0 ? (
        <div className="activity-clean">
          <p className="activity-clean-title">{t('activity.checkCleanTitle')}</p>
          <p className="activity-clean-body">{t('activity.checkCleanBody')}</p>
        </div>
      ) : null}

      {errors.length === 0 ? null : (
        <div className="activity-group">
          <p className="ndrc-label">
            {errors.length === 1
              ? t('activity.checkErrorsOne')
              : t('activity.checkErrorsMany', { n: errors.length })}
          </p>
          <FindingList findings={errors} />
        </div>
      )}

      {warnings.length === 0 ? null : (
        <div className="activity-group">
          <p className="ndrc-label">
            {warnings.length === 1
              ? t('activity.checkWarningsOne')
              : t('activity.checkWarningsMany', { n: warnings.length })}
          </p>
          <FindingList findings={warnings} />
        </div>
      )}

      <JudgmentReport review={review} />

      <div className="activity-scope">
        <div className="activity-group">
          <p className="ndrc-label">{t('activity.checkVerified')}</p>
          <ul className="activity-scope-list">
            {looked.map((line) => (
              <li key={line}>
                <Icon name="check" size={12} strokeWidth={2.2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="activity-group">
          <p className="ndrc-label">{t('activity.checkNotVerified')}</p>
          <ul className="activity-scope-list activity-scope-list--pending">
            {notLooked.map((line) => (
              <li key={line}>
                <span className="activity-dot activity-dot--warning" aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="ndrc-hint">{t('activity.checkNotVerifiedHint')}</p>
        </div>
      </div>
    </section>
  );
}

/**
 * **Les remarques de l'IA, à côté des constats automatiques et jamais confondues avec eux.**
 *
 * Deux sources, deux statuts, et l'élève doit pouvoir les distinguer d'un coup d'œil :
 *
 * - au-dessus, ce qui est **mal écrit**, trouvé par des règles : une adresse sans « @ » est fausse
 *   pour tout le monde, toujours. Pastille rouge ou ambre, le vocabulaire de la correction.
 * - ici, ce qui **semble douteux** à un modèle de langue : une opinion, utile et faillible. Bloc
 *   distinct, encre violette, et un titre qui dit « à vérifier vous-même » plutôt que « à
 *   corriger ». Aucune de ces phrases ne doit pouvoir se lire comme une faute constatée.
 *
 * Les deux portent pourtant le MÊME type (`CheckFinding`) : c'est `source: 'judgment'` qui les
 * sépare, exactement la place que `checks/contacts.ts` réservait depuis le premier jour.
 *
 * `null` (aucun clic depuis l'ouverture) n'affiche rien — surtout pas un cadre vide qui laisserait
 * croire qu'une relecture a eu lieu.
 */
function JudgmentReport({ review }: { review: ReviewState }): JSX.Element | null {
  if (review.busy) {
    return (
      <section className="activity-ai">
        <p className="activity-ai-title">{t('activity.aiTitle')}</p>
        <p className="activity-ai-body">{t('activity.aiBusy')}</p>
      </section>
    );
  }
  if (review.error !== null) {
    return (
      <section className="activity-ai">
        <p className="activity-ai-title">{t('activity.aiTitle')}</p>
        <p className="activity-ai-body">{review.error}</p>
      </section>
    );
  }
  const result = review.result;
  if (result === null) return null;

  // Refus attendu (aucune clé, plafond du jour atteint, usage coupé, fournisseur en panne) : le
  // serveur a déjà écrit la phrase française qui dit quoi faire, et rappelle que la vérification
  // de la forme, elle, continue. Elle est affichée telle quelle, jamais réécrite ici.
  if (result.status === 'unavailable') {
    return (
      <section className="activity-ai">
        <p className="activity-ai-title">{t('activity.aiTitle')}</p>
        <p className="activity-ai-body">{result.message}</p>
      </section>
    );
  }

  return (
    <section className="activity-ai">
      <p className="activity-ai-title">{t('activity.aiTitle')}</p>
      <p className="activity-ai-body">{t('activity.aiIntro')}</p>

      {result.findings.length === 0 ? (
        <p className="activity-ai-body">{t('activity.aiNothing')}</p>
      ) : (
        <ul className="activity-findings">
          {result.findings.map((finding, index) => (
            <li
              key={`${finding.subjectId}:${String(index)}`}
              className="activity-finding activity-finding--ai"
            >
              <span className="activity-dot activity-dot--judgment" aria-hidden="true" />
              <span className="activity-ai-remark">
                <span className="activity-ai-subject">{finding.subjectLabel}</span>
                <span>{finding.message}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Pas de seconde liste « ce que l'IA a regardé » ici : ses points figurent déjà, une seule
          fois, dans « Ce qui a été relu » plus bas — ils y sont PASSÉS quand la relecture a eu
          lieu. Les répéter ferait dire deux fois la même chose à un panneau dont tout l'intérêt
          est qu'on puisse le lire en entier. */}
    </section>
  );
}

export function ActivityBoard() {
  return (
    <>
      <InteractiveBoard />
      <LegacyActivityBoard />
    </>
  );
}
